import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  input,
  model,
  OnDestroy,
  OnInit,
  Optional,
  signal,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApiS3Service } from 'src/app/services/api-s3.service';
import { ApiUsuarioService } from 'src/app/services/api-usuario.service';
import { FiltroControllerService } from 'src/app/services/filtro-controller.service';
import { ModulosDeFormulario } from 'src/app/utils/formsModules';
import { FiltroComponent } from '../../filtro.component';

@Component({
  selector: 'sub-filtro-usuarios',
  templateUrl: './usuarios.component.html',
  styleUrls: ['../../filtro.component.scss'],
  imports: [
    CommonModule,
    ModulosDeFormulario,
    MatCheckboxModule,
    MatRadioModule,
    MatMenuModule,
  ],
})
export class UsuariosComponent implements OnInit, OnDestroy {
  filtroUsuario = model<string[]>([]);
  @Input('isEmail') isEmail: boolean = false;
  maxSelection = input<number | null>(null);

  private readonly LIMIT = 30;
  private readonly MIN_BUSCA = 3;

  private _corretoraId: string | null = null;
  @Input() set corretoraId(value: string | null) {
    const novo = value || null;
    if (novo === this._corretoraId) return;
    this._corretoraId = novo;
    if (this._iniciado) this.carregar(true);
  }

  private apiUsuario = inject(ApiUsuarioService);
  private apiS3 = inject(ApiS3Service);

  protected usuarios = signal<any[]>([]);
  protected carregando = signal<boolean>(false);
  protected erro = signal<boolean>(false);
  protected fimDaLista = signal<boolean>(false);

  private page = 1;
  private busca = '';
  private requestId = 0;
  private _iniciado = false;
  private buscaInput$ = new Subject<string>();
  private subscription = new Subscription();

  filteredUsuariosComEstado = computed(() => {
    const maxSelection = this.maxSelection();
    const filtro = this.filtroUsuario() ?? [];
    const limiteAtingido = maxSelection != null && filtro.length >= maxSelection;

    return this.usuarios().map((u) => {
      const value = this.isEmail ? u.email : u.id;
      const checked = filtro.includes(value);
      return {
        ...u,
        img: this.apiUsuario.avatares[u.id] || '',
        checked,
        disabled: limiteAtingido && !checked,
      };
    });
  });

  limiteAtingido = computed(() => {
    const maxSelection = this.maxSelection();
    const filtroUsuario = this.filtroUsuario();
    return maxSelection != null && (filtroUsuario?.length ?? 0) >= maxSelection;
  });

  constructor(
    private filtroService: FiltroControllerService,
    @Optional() private filtroComp?: FiltroComponent
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.buscaInput$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((valor) => {
          this.busca = valor;
          this.carregar(true);
        })
    );
    this._iniciado = true;
    this.carregar(true);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private async carregar(reset: boolean): Promise<void> {
    if (reset) {
      this.page = 1;
      this.fimDaLista.set(false);
      this.usuarios.set([]);
    } else if (this.fimDaLista() || this.carregando()) {
      return;
    }

    const reqId = ++this.requestId;
    this.carregando.set(true);
    this.erro.set(false);

    try {
      const buscaValida =
        this.busca && this.busca.length >= this.MIN_BUSCA
          ? this.busca
          : undefined;

      const ret = await this.apiUsuario.buscaUsuariosPaginado({
        page: this.page,
        limit: this.LIMIT,
        busca: buscaValida,
        corretoraId: this._corretoraId || undefined,
      });

      if (reqId !== this.requestId) return;

      const data = ret?.data ?? [];
      this.fimDaLista.set(data.length < this.LIMIT);
      this.usuarios.update((prev) => (reset ? data : [...prev, ...data]));
      this.carregarAvatares(data);
    } catch (error) {
      if (reqId !== this.requestId) return;
      console.error('Erro ao buscar usuários do filtro:', error);
      this.erro.set(true);
    } finally {
      if (reqId === this.requestId) this.carregando.set(false);
    }
  }

  private async carregarAvatares(usuarios: any[]): Promise<void> {
    const pendentes = usuarios.filter((u) => !this.apiUsuario.avatares[u.id]);
    if (!pendentes.length) return;

    await Promise.all(
      pendentes.map(async (u) => {
        try {
          this.apiUsuario.avatares[u.id] = await this.apiS3.getAvatarUsuario(
            u.id
          );
        } catch {
          this.apiUsuario.avatares[u.id] = '';
        }
      })
    );

    // Refaz a referência do array para recomputar os avatares no template.
    this.usuarios.update((arr) => [...arr]);
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (this.carregando() || this.fimDaLista()) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      this.page++;
      this.carregar(false);
    }
  }

  onFiltroChange(user) {
    const value = this.isEmail ? user.email : user.id;
    const atual = this.filtroUsuario() ?? [];
    const jaSelecionado = atual.includes(value);

    if (!jaSelecionado && this.limiteAtingido()) {
      return;
    }

    const novo = jaSelecionado
      ? atual.filter((x) => x !== value)
      : [...atual, value];

    this.filtroUsuario.set(novo);

    this.filtroService.updateActiveFilters('users', novo.length);

    this.filtroComp?.sendFilter(false);
  }

  onBuscaChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.buscaInput$.next(input.value.toLowerCase().trim());
  }
}
